import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  ClientConfig,
  defaultClientConfig,
  getClientConfigByBrand,
  resolveClientConfig,
} from "./clientConfig";
import { fetchBrandBySlug } from "./brandConfigApi";

type BrandingContextValue = {
  brandId: string;
  config: ClientConfig;
  isShowcase: boolean;
  pendingConfig: boolean;
  configError: string | null;
};

const BrandingContext = createContext<BrandingContextValue>({
  brandId: defaultClientConfig.id,
  config: defaultClientConfig,
  isShowcase: false,
  pendingConfig: false,
  configError: null,
});

type BrandProviderProps = {
  brandSlug?: string;
  isShowcase?: boolean;
  children: React.ReactNode;
};

export const BrandProvider: React.FC<BrandProviderProps> = ({
  brandSlug,
  isShowcase = false,
  children,
}) => {
  const localConfig = useMemo(() => getClientConfigByBrand(brandSlug), [brandSlug]);
  const [config, setConfig] = useState<ClientConfig>(localConfig);
  const [pendingConfig, setPendingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setConfig(localConfig);
    setConfigError(null);

    if (!brandSlug) {
      return () => {
        cancelled = true;
      };
    }

    setPendingConfig(true);

    fetchBrandBySlug(brandSlug)
      .then((record) => {
        if (cancelled || !record?.brand_data) {
          return;
        }

        setConfig(resolveClientConfig(record.brand_data));
      })
      .catch((error) => {
        if (!cancelled) {
          setConfigError(
            error instanceof Error ? error.message : "Не удалось загрузить конфиг бренда.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPendingConfig(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [brandSlug, localConfig]);

  const value = useMemo(
    () => ({
      brandId: config.id,
      config,
      isShowcase,
      pendingConfig,
      configError,
    }),
    [config, configError, isShowcase, pendingConfig],
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
